import { Injectable } from "@angular/core";
import { defer, Observable, Subject } from "rxjs";
import { finalize, tap } from "rxjs/operators";
import { GuidUtils } from "~app/common/utils/guid-utils";

export interface IBusyChannel {
  channel: string;
  label: string;
  isWorking: Subject<boolean>;
  scopes: string[];
}

@Injectable({
    providedIn: "root"
})
export class BusyService {
    private readonly channels: IBusyChannel[] = [];

    // eslint-disable-next-line sonarjs/public-static-readonly
    static defaultLabel = "Vänta..";

    addWork<T>(observable: Observable<T>, channelName = null, label = null): Observable<T> {
        const scopeId = GuidUtils.newGuid();
        return defer(() => {
            this.startScope(channelName, scopeId, label);
            return observable.pipe(
                tap({
                    next: () => this.stopScope(channelName, scopeId),
                    error: () => this.stopScope(channelName, scopeId),
                    complete: () => this.stopScope(channelName, scopeId)
                }),
                finalize(() => this.stopScope(channelName, scopeId)));
        });
    }

    getChannel(channelName = null): IBusyChannel {
        let channel = this.findChannel(channelName);
        if (channel == null) {
            channel = this.createChannel(channelName);
        }
        return channel;
    }

    stopWork(channelName: string): void {
        const channel = this.getChannel(channelName);
        channel.scopes = [];
        channel.isWorking.next(false);
    }

    private startScope(channelName: string, scopeId: string, label: string): void {
        const channel = this.getChannel(channelName);
        this.setLabel(channel, label);
        channel.scopes.push(scopeId);
        channel.isWorking.next(true);
    }

    private stopScope(channelName: string, scopeId: string): void {
        const channel = this.getChannel(channelName);
        const scopePos = channel.scopes.indexOf(scopeId);
        if (scopePos > -1) {
            channel.scopes.splice(channel.scopes.indexOf(scopeId), 1);
        }
        if (channel.scopes.length === 0) {
            channel.isWorking.next(false);
        }
    }

    private findChannel(channelName?: string): IBusyChannel {
        const found = this.channels.filter(x => x.channel === (channelName ?? ""));
        return (found && found.length > 0) ? found[0] : null;
    }

    private createChannel(channelName?: string): IBusyChannel {
        const newChannel = {
            channel: (channelName ?? ""),
            isWorking: new Subject<boolean>(),
            count: 0,
            label: "",
            scopes: []
        } as IBusyChannel;
        this.channels.push(newChannel);
        return newChannel;
    }

    private setLabel(channel: IBusyChannel, label: string): void {
        let newlabel = label != null && label !== "" ? label : channel.label;
        if (newlabel === "") {
            newlabel = BusyService.defaultLabel;
        }
        channel.label = newlabel;
    }

}
